import { useState } from 'react';
import './App.css';
import {
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

function App() {
  const [dobValue, setDobValue] = useState(dayjs());
  const [isDeferredEntry, setIsDeferredEntry] = useState(false);
  const [isCovidYears, setIsCovidYears] = useState(false);

  // Base Year Calculation - Assumes Reception Year as 0
  const birthMonth = dobValue?.format('MM');
  const birthYear = dobValue?.format('YYYY');
  const baseYear = isDeferredEntry
    ? +birthYear + (birthMonth < 9 ? 5 : 6)
    : +birthYear + (birthMonth < 9 ? 4 : 5);

  // Current Academic Year Identification - September New Academic Year
  const currentDate = dayjs(new Date());
  const currentMonth = currentDate.format('MM');
  const currentYear = currentDate.format('YYYY');
  const currentStartingAcademicYear =
    currentMonth < 9 ? +currentYear - 1 : +currentYear;

  const rows = [
    {
      id: 0,
      childAge: '4-5',
      keyStage: 'EYFS',
      year: 0,
      yearofEntry: baseYear || '-',
    },
    {
      id: 1,
      childAge: '5-6',
      keyStage: 1,
      year: 1,
      yearofEntry: baseYear + 1 || '-',
    },
    {
      id: 2,
      childAge: '6-7',
      keyStage: 1,
      year: 2,
      yearofEntry: baseYear + 2 || '-',
    },
    {
      id: 3,
      childAge: '7-8',
      keyStage: 2,
      year: 3,
      yearofEntry: baseYear + 3 || '-',
    },
    {
      id: 4,
      childAge: '8-9',
      keyStage: 2,
      year: 4,
      yearofEntry: baseYear + 4 || '-',
    },
    {
      id: 5,
      childAge: '9-10',
      keyStage: 2,
      year: 5,
      yearofEntry: baseYear + 5 || '-',
    },
    {
      id: 6,
      childAge: '10-11',
      keyStage: 2,
      year: 6,
      yearofEntry: baseYear + 6 || '-',
    },
    {
      id: 7,
      childAge: '11-12',
      keyStage: 3,
      year: 7,
      yearofEntry: baseYear + 7 || '-',
    },
    {
      id: 8,
      childAge: '12-13',
      keyStage: 3,
      year: 8,
      yearofEntry: baseYear + 8 || '-',
    },
    {
      id: 9,
      childAge: '13-14',
      keyStage: 3,
      year: 9,
      yearofEntry: baseYear + 9 || '-',
    },
    {
      id: 10,
      childAge: '14-15',
      keyStage: 4,
      year: 10,
      yearofEntry: baseYear + 10 || '-',
    },
    {
      id: 11,
      childAge: '15-16',
      keyStage: 4,
      year: 11,
      yearofEntry: baseYear + 11 || '-',
    },
    {
      id: 12,
      childAge: '16-17',
      keyStage: 5,
      year: 12,
      yearofEntry: baseYear + 12 || '-',
    },
    {
      id: 13,
      childAge: '17-18',
      keyStage: 5,
      year: 13,
      yearofEntry: baseYear + 13 || '-',
    },
  ];

  function getBackgroundColor(yearofEntry) {
    if (yearofEntry === currentStartingAcademicYear) return '#AAFABE';
    if (isCovidYears && yearofEntry === 2019) return '#FBA9A9';
    if (isCovidYears && yearofEntry === 2020) return '#FBA9A9';
  }

  const handleDeferredEntryCheckbox = (event) => {
    setIsDeferredEntry(event.target.checked);
  };

  const handleCovidCheckbox = (event) => {
    setIsCovidYears(event.target.checked);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container
        maxWidth={false}
        sx={{
          maxWidth: '450px',
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          marginBottom: '20px',
        }}
      >
        <Card raised sx={{ width: '100%', height: '100%' }}>
          <CardContent>
            <Typography
              component="h1"
              variant="h5"
              align="center"
              noWrap
              textTransform="uppercase"
              fontWeight="400"
            >
              School Age Calculator
            </Typography>
            <Stack direction="column" marginTop={3}>
              <DesktopDatePicker
                disableFuture={true}
                closeOnSelect={true}
                label="Enter DOB"
                inputFormat="DD/MM/YYYY"
                value={dobValue}
                onChange={(newValue) => {
                  setDobValue(newValue);
                }}
                renderInput={(params) => <TextField {...params} type="date" />}
              />
              <FormControlLabel
                value="start"
                control={
                  <Checkbox
                    onChange={handleDeferredEntryCheckbox}
                    color="primary"
                  />
                }
                label="Deferred Entry"
                labelPlacement="end"
              />
              <FormControlLabel
                value="start"
                control={
                  <Checkbox onChange={handleCovidCheckbox} color="primary" />
                }
                label="Highlight Covid Years"
                labelPlacement="end"
              />
            </Stack>
            <TableContainer>
              <Table size="medium">
                <TableHead
                  sx={{
                    backgroundColor: '#F8F8F8',
                  }}
                >
                  <TableRow>
                    <TableCell align="center">Child's Age</TableCell>
                    <TableCell align="center">Key Stage</TableCell>
                    <TableCell align="center">Year</TableCell>
                    <TableCell align="center">Year of Entry</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        backgroundColor: getBackgroundColor(row.yearofEntry),
                      }}
                    >
                      <TableCell align="center">{row.childAge}</TableCell>
                      <TableCell align="center">{row.keyStage}</TableCell>
                      <TableCell align="center">{row.year}</TableCell>
                      <TableCell align="center">{row.yearofEntry}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>
    </LocalizationProvider>
  );
}

export default App;
